import type {
  BoardItemProjection,
  Capability,
  CrewProjection,
  ForgeRemoteFixture,
  ManagerMessageProjection,
  MemberProjection,
  PermissionPreset,
  RunProjection,
  WorkItemReference,
} from "../domain";

const capabilitiesByPreset: Record<PermissionPreset, Capability[]> = {
  observe: ["projection.read", "evidence.read"],
  custom: ["projection.read"],
  converse: ["projection.read", "evidence.read", "manager.chat", "attention.ack"],
  operate: [
    "projection.read",
    "evidence.read",
    "manager.chat",
    "attention.ack",
    "board.edit",
    "decision.respond",
    "run.pause",
    "run.resume",
  ],
};

const CORTEX_CREW_ID = "crew_00000000000000000000000000000001";
const CORTEX_BOARD_ID = "board_00000000000000000000000000000001";
const FORGE_CREW_ID = "crew_00000000000000000000000000000002";
const FORGE_BOARD_ID = "board_00000000000000000000000000000002";
const EMAIL_CREW_ID = "crew_00000000000000000000000000000003";
const EMAIL_BOARD_ID = "board_00000000000000000000000000000003";
const SANITY_CREW_ID = "crew_00000000000000000000000000000004";
const SANITY_BOARD_ID = "board_00000000000000000000000000000004";

function ago(now: number, milliseconds: number): string {
  return new Date(now - milliseconds).toISOString();
}

function member(
  now: number,
  id: string,
  name: string,
  role: string,
  health: MemberProjection["health"],
  currentWork: string,
  ageSeconds = 20,
): MemberProjection {
  return { id, name, role, health, currentWork, lastBeatAt: ago(now, ageSeconds * 1_000) };
}

function workItem(crewId: string, boardId: string, cardId: string): WorkItemReference {
  return { crewId, boardId, cardId, goalId: `goal-${cardId}` };
}

function run(
  id: string,
  item: WorkItemReference,
  title: string,
  state: RunProjection["state"],
  progress: number,
  nextCheckpoint: string,
  steps: string[],
  doneSteps: number,
): RunProjection {
  return {
    id,
    workItem: item,
    title,
    revision: 18,
    state,
    progress,
    nextCheckpoint,
    allowedActions: state === "running" ? ["pause"] : state === "paused" ? ["resume"] : [],
    plan: steps.map((step, index) => ({
      id: `${id}-step-${index + 1}`,
      title: step,
      state: index < doneSteps ? "done" : index === doneSteps ? "current" : "pending",
    })),
  };
}

function boardItem(
  crewId: string,
  boardId: string,
  id: string,
  title: string,
  status: BoardItemProjection["status"],
  priority: BoardItemProjection["priority"],
  assigneeId: string,
  summary: string,
): BoardItemProjection {
  const canonicalStatuses: BoardItemProjection["allowedStatusTransitions"] = ["backlog", "next", "in-progress", "blocked", "review", "done"];
  return {
    id,
    ...workItem(crewId, boardId, id),
    revision: 18,
    title,
    summary,
    status,
    priority,
    assigneeId,
    checkpointAt: null,
    operatorNote: "",
    remoteEditableFields: ["status", "priority", "assignee", "operatorNote"],
    allowedStatusTransitions: status === "blocked"
      ? ["blocked", "in-progress", "backlog"]
      : [status, ...canonicalStatuses.filter((value) => value !== status)],
  };
}

function message(
  now: number,
  id: string,
  authorType: ManagerMessageProjection["authorType"],
  authorLabel: string,
  text: string,
  minutesAgo: number,
  reference?: string,
): ManagerMessageProjection {
  return { id, authorType, authorLabel, text, reference, createdAt: ago(now, minutesAgo * 60_000) };
}

function createCrews(now: number): CrewProjection[] {
  return [
    {
      id: CORTEX_CREW_ID,
      name: "Cortex Sync",
      shortName: "Cortex",
      revision: 321,
      boardId: CORTEX_BOARD_ID,
      boardRevision: 18,
      runState: "waiting",
      health: "needs-input",
      summary: "Recovery extraction is ready and waiting for your approval.",
      since: {
        from: ago(now, 44 * 60_000),
        countsAvailable: true,
        eventCount: 32,
        completedCount: 4,
        decisionCount: 1,
        summary: "Four tasks finished; extraction is waiting for approval.",
      },
      members: [
        member(now, "cortex-manager", "Crew Manager", "manager", "waiting", "Awaiting extraction approval"),
        member(now, "architect", "Architect", "review", "online", "Recovery boundary verified", 33),
        member(now, "coder-sync", "Coder 1", "implementation", "waiting", "Ready for extraction", 46),
      ],
      runs: [
        run(
          "run-p3r1",
          workItem(CORTEX_CREW_ID, CORTEX_BOARD_ID, "p3-r1"),
          "P3-R1 recovery extraction",
          "waiting",
          56,
          "Operator approval",
          [
            "Verify recovery boundary against held checkouts",
            "Confirm matching commits for the policy set",
            "Extract matching policy work",
            "Run cumulative validation gates",
            "Hold before push for operator review",
          ],
          2,
        ),
      ],
      board: [
        boardItem(CORTEX_CREW_ID, CORTEX_BOARD_ID, "p3-r1", "Extract matching LaniOpen policy work", "blocked", "urgent", "architect", "Boundary verified; approval remains."),
        boardItem(CORTEX_CREW_ID, CORTEX_BOARD_ID, "p3-02", "Validate operation-ID deduplication", "next", "high", "coder-sync", "Ready after extraction closes."),
        boardItem(CORTEX_CREW_ID, CORTEX_BOARD_ID, "p3-01", "Establish synchronization ledger", "done", "normal", "architect", "Ledger validation is green."),
      ],
      manager: {
        status: "available",
        cursor: "manager-transcript-v1:2",
        revision: 2,
        historyTruncated: false,
        messages: [
          message(now, "cx-1", "manager", "Crew Manager", "Architect verified the recovery boundary. I am holding before extraction as requested.", 8, "p3-r1"),
          message(now, "cx-2", "member", "Architect", "Both held checkouts match their expected revisions. Approval is the remaining gate.", 3, "dec-p3r1"),
        ],
      },
      decisions: [
        {
          id: "dec-p3r1",
          workItem: workItem(CORTEX_CREW_ID, CORTEX_BOARD_ID, "p3-r1"),
          revision: 7,
          title: "Approve extraction to continue",
          consequence: "The manager will start the bounded extraction and stop again before any push.",
          requestedBy: "Architect",
          requestedAt: ago(now, 3 * 60_000),
          runId: "run-p3r1",
          evidenceIds: ["evidence-boundary", "evidence-commits"],
          state: "waiting",
        },
      ],
      evidence: [
        { id: "evidence-boundary", occurredAt: ago(now, 6 * 60_000), actor: "Architect", finding: "Recovery boundary diff reviewed; no held checkout changes detected." },
        { id: "evidence-commits", occurredAt: ago(now, 3 * 60_000), actor: "Architect", finding: "Matching commits and branch revisions verified." },
      ],
      systems: [
        { id: "system-prime", label: "Lani Devbox", kind: "prime-host", state: "reachable" },
        { id: "system-analytics", label: "Analytics worker", kind: "remote-worker", state: "reachable" },
      ],
      capabilityProviders: [
        { id: "provider-lani-code", label: "Lani Code", providerType: "lani-code", capabilities: ["code.read", "graph.execute"], systemId: "system-prime" },
        { id: "provider-git", label: "Git Analytics", providerType: "git-analytics", capabilities: ["git.read", "git.analytics"], systemId: "system-analytics" },
      ],
      graphs: [
        { id: "graph-lani-code", label: "Recovery coding", graphType: "lani-code", state: "online", systemId: "system-prime", capabilityProviderIds: ["provider-lani-code"], resourceIds: ["resource-laniopen", "resource-cortex"] },
        { id: "graph-git-analytics", label: "Cortex repository analytics", graphType: "git-analytics", state: "online", systemId: "system-analytics", capabilityProviderIds: ["provider-git"], resourceIds: ["resource-laniopen", "resource-cortex"] },
      ],
      resources: [
        { id: "resource-laniopen", label: "LaniCorp/LaniOpen", resourceType: "git-repository", systemId: "system-prime" },
        { id: "resource-cortex", label: "cortex", resourceType: "git-branch", repositoryId: "resource-laniopen", branchName: "cortex", systemId: "system-prime" },
      ],
      memberGraphPlacements: [
        { memberId: "architect", graphId: "graph-lani-code", placementRole: "review" },
        { memberId: "coder-sync", graphId: "graph-lani-code", placementRole: "implementation" },
      ],
    },
    {
      id: FORGE_CREW_ID,
      name: "Forge Crews Control Plane",
      shortName: "Forge CP",
      revision: 207,
      boardId: FORGE_BOARD_ID,
      boardRevision: 18,
      runState: "running",
      health: "healthy",
      summary: "Projection and command acknowledgement work is moving normally.",
      since: { from: ago(now, 44 * 60_000), countsAvailable: true, eventCount: 18, completedCount: 2, decisionCount: 0, summary: "Two tests landed; acknowledgement wiring is 68% complete." },
      members: [
        member(now, "forge-manager", "Crew Manager", "manager", "online", "Coordinating CP-1.1", 12),
        member(now, "coder-1", "Coder 1", "implementation", "busy", "Effective-event boundary", 20),
        member(now, "forge-reviewer", "Reviewer", "review", "online", "Projection contract review", 41),
      ],
      runs: [
        run("run-cp11", workItem(FORGE_CREW_ID, FORGE_BOARD_ID, "cp-1.1"), "CP-1.1 projection slice", "running", 68, "Effective-event assertion", ["Slice projection surface", "Wire acknowledgement receipt", "Assert effective event", "Prepare CP-1.2 handoff"], 2),
      ],
      board: [
        boardItem(FORGE_CREW_ID, FORGE_BOARD_ID, "cp-1.1", "Projection and command acknowledgement", "in-progress", "high", "coder-1", "Focused Debug tests are green."),
        boardItem(FORGE_CREW_ID, FORGE_BOARD_ID, "cp-1.2", "Remote projection subscription seam", "next", "normal", "forge-reviewer", "Ready after CP-1.1."),
      ],
      manager: {
        status: "available",
        cursor: "manager-transcript-v1:1",
        revision: 1,
        historyTruncated: false,
        messages: [
          message(now, "fg-1", "manager", "Crew Manager", "Focused tests are green. Coder 1 is connecting accepted commands to effective events.", 11, "cp-1.1"),
        ],
      },
      decisions: [],
      evidence: [{ id: "evidence-cp", occurredAt: ago(now, 11 * 60_000), actor: "Reviewer", finding: "Projection contract tests passed." }],
    },
    {
      id: EMAIL_CREW_ID,
      name: "Email Subsystem",
      shortName: "Email",
      revision: 94,
      boardId: EMAIL_BOARD_ID,
      boardRevision: 18,
      runState: "running",
      health: "at-risk",
      summary: "SMTP readiness has failed twice; one bounded retry remains.",
      since: { from: ago(now, 44 * 60_000), countsAvailable: true, eventCount: 11, completedCount: 1, decisionCount: 0, summary: "Core handler passed; integration is on its final retry." },
      members: [
        member(now, "email-manager", "Crew Manager", "manager", "online", "Watching bounded retry", 18),
        member(now, "coder-2", "Coder 2", "implementation", "busy", "SMTP readiness retry", 26),
        member(now, "test-runner", "Test Runner", "verification", "stale", "Container readiness", 222),
      ],
      runs: [run("run-email", workItem(EMAIL_CREW_ID, EMAIL_BOARD_ID, "email-42"), "Email integration slice", "running", 74, "Final readiness retry", ["Rebuild readiness probe", "Run bounded retry", "Verify outbound queue", "Attach evidence"], 1)],
      board: [
        boardItem(EMAIL_CREW_ID, EMAIL_BOARD_ID, "email-42", "Stabilize SMTP container readiness", "in-progress", "high", "coder-2", "Two timeouts; one bounded retry remains."),
        boardItem(EMAIL_CREW_ID, EMAIL_BOARD_ID, "email-38", "Complete handler contract tests", "done", "normal", "coder-2", "Focused contract tests pass."),
      ],
      manager: {
        status: "available",
        cursor: "manager-transcript-v1:1",
        revision: 1,
        historyTruncated: false,
        messages: [message(now, "em-1", "manager", "Crew Manager", "The readiness probe timed out twice. I will stop for input if the final retry fails.", 6, "EMAIL-42")],
      },
      decisions: [],
      evidence: [],
    },
    {
      id: SANITY_CREW_ID,
      name: "Sanity Preview",
      shortName: "Sanity",
      revision: 151,
      boardId: SANITY_BOARD_ID,
      boardRevision: 18,
      runState: "completed",
      health: "completed",
      summary: "Preview-state review completed with no blocking regressions.",
      since: { from: ago(now, 44 * 60_000), countsAvailable: true, eventCount: 23, completedCount: 3, decisionCount: 0, summary: "Empty, configured and full states were approved." },
      members: [member(now, "sanity-manager", "Crew Manager", "manager", "online", "Preparing handoff", 50), member(now, "sanity-reviewer", "Reviewer", "review", "online", "Review complete", 64)],
      runs: [run("run-sanity", workItem(SANITY_CREW_ID, SANITY_BOARD_ID, "sanity-18"), "Sanity preview-state review", "completed", 100, "Handoff", ["Capture empty state", "Capture configured state", "Capture full state", "Approve handoff"], 4)],
      board: [boardItem(SANITY_CREW_ID, SANITY_BOARD_ID, "sanity-18", "Review preview states", "done", "normal", "sanity-reviewer", "All states approved with evidence.")],
      manager: {
        status: "available",
        cursor: "manager-transcript-v1:1",
        revision: 1,
        historyTruncated: false,
        messages: [message(now, "sn-1", "manager", "Crew Manager", "Reviewer approved all preview states. Nothing here needs you.", 22, "SANITY-18")],
      },
      decisions: [],
      evidence: [{ id: "evidence-sanity", occurredAt: ago(now, 22 * 60_000), actor: "Reviewer", finding: "Empty, configured and full screenshots approved." }],
    },
  ];
}

export function createFixture(preset: PermissionPreset): ForgeRemoteFixture {
  const now = Date.now();
  const crews = createCrews(now);
  return {
    session: {
      sessionId: "rmt-fixture-01",
      deviceLabel: "Foldable phone",
      hostLabel: "Lani Devbox · Prime",
      preset,
      capabilities: capabilitiesByPreset[preset],
      expiresAt: ago(now, -8 * 60 * 60_000),
      gateway: { state: "connected", lastConnectedAt: ago(now, 2_000) },
      host: { state: "reachable", lastSeenAt: ago(now, 8_000) },
      projection: { state: "live", snapshotAt: ago(now, 12_000), sourceEventAt: ago(now, 18_000), cursor: "cur-1842" },
      commandAuthority: {
        state: "unavailable",
        reason: "Fixture preview only: no authoritative Forge command channel is connected.",
      },
    },
    crews,
    attention: [
      { id: "att-decision", crewId: CORTEX_CREW_ID, workItem: workItem(CORTEX_CREW_ID, CORTEX_BOARD_ID, "p3-r1"), kind: "decision", rank: 982, title: "Approve extraction to continue", summary: "The recovery boundary is verified; the manager is holding before extraction.", occurredAt: ago(now, 3 * 60_000), actor: "Architect", objectType: "decision", objectId: "dec-p3r1", acknowledged: false, isNew: true },
      { id: "att-email", crewId: EMAIL_CREW_ID, workItem: workItem(EMAIL_CREW_ID, EMAIL_BOARD_ID, "email-42"), kind: "risk", rank: 810, title: "Integration test failed twice", summary: "SMTP readiness timed out. One bounded retry remains before the Crew stops for input.", occurredAt: ago(now, 6 * 60_000), actor: "Coder 2", objectType: "board-item", objectId: "email-42", acknowledged: false, isNew: true },
      { id: "att-running", crewId: FORGE_CREW_ID, workItem: workItem(FORGE_CREW_ID, FORGE_BOARD_ID, "cp-1.1"), kind: "running", rank: 540, title: "Control-plane slice is 68% complete", summary: "Projection tests are green; effective-event wiring is next.", occurredAt: ago(now, 11 * 60_000), actor: "Coder 1", objectType: "run", objectId: "run-cp11", acknowledged: true, isNew: false },
      { id: "att-done", crewId: SANITY_CREW_ID, workItem: workItem(SANITY_CREW_ID, SANITY_BOARD_ID, "sanity-18"), kind: "completed", rank: 310, title: "Preview-state review completed", summary: "No blocking visual regressions remain.", occurredAt: ago(now, 22 * 60_000), actor: "Reviewer", objectType: "board-item", objectId: "sanity-18", acknowledged: false, isNew: false },
    ],
  };
}

export function fixturePresetFromSearch(search: string): PermissionPreset {
  const value = new URLSearchParams(search).get("preset")?.toLowerCase();
  return value === "converse" || value === "operate" ? value : "observe";
}
